/**
 * THE RIDGE TEXTURE AS A SHORT URL (B10, QA L2-06 / L2-07).
 *
 * The design system exposes the ridge as `--ridge: url(data:image/png;base64,…)`, about 215 KB of
 * text (design/ridge.ts). Every `.ld-rake::after` (rows, chips, buttons, icon buttons) masks with
 * `var(--ridge)`, and Chrome substitutes and parses that string again for each element whose style
 * is recalculated. Making the panel inert for a drive recalculates ~50 of them: 150–900 ms of style
 * work in one frame, measured on a real GPU. The same image as a `blob:` URL is ~70 characters, and
 * the same recalculation takes ~15 ms.
 *
 * The layout root carries the short form, so everything in the app inherits it; the loader and any
 * page without the layout keep the design system's own value. (The lasting fix belongs in
 * design/ridge.ts: canvas.toBlob → URL.createObjectURL. This is harmless once that lands.)
 */
import { ensureRidge } from "@/design";

let blobUrl: string | null = null;

/** The ridge texture as a blob: URL (rendered once per page), or null without a DOM or Blob URLs. */
export function ridgeBlobUrl(): string | null {
  if (blobUrl) return blobUrl;
  if (typeof document === "undefined" || typeof URL === "undefined" || typeof URL.createObjectURL !== "function" || typeof atob !== "function") return null;
  const data = ensureRidge();
  if (!data || !data.startsWith("data:")) return null;
  const comma = data.indexOf(",");
  const head = data.slice(5, comma); // "image/png;base64"
  if (!head.endsWith(";base64")) return null;
  try {
    const bin = atob(data.slice(comma + 1));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    blobUrl = URL.createObjectURL(new Blob([bytes], { type: head.slice(0, -7) || "image/png" }));
  } catch {
    return null;
  }
  return blobUrl;
}

/**
 * Sets the short ridge on `el` after the first paint (the texture's first render costs ~150 ms, so it
 * waits as the loader does). Returns a cancel function.
 */
export function applyRidgeBlob(el: HTMLElement): () => void {
  if (typeof requestAnimationFrame !== "function") return () => undefined;
  let t: ReturnType<typeof setTimeout> | undefined;
  const raf = requestAnimationFrame(() => {
    t = setTimeout(() => {
      const url = ridgeBlobUrl();
      if (url) el.style.setProperty("--ridge", `url(${url})`);
    }, 0);
  });
  return () => {
    cancelAnimationFrame(raf);
    if (t) clearTimeout(t);
  };
}
